import React from "react";
import { Link } from "react-router-dom";
import {
  Phone,
  Mail,
  MapPin,
  Clock,
  Facebook,
  Linkedin,
  Twitter,
  ArrowUpRight,
} from "lucide-react";
import "./SiteFooter.css";

const QUICK_LINKS = [
  { to: "/", label: "Home" },
  { to: "/about", label: "About" },
  { to: "/industries", label: "Industries" },
  { to: "/services", label: "Services" },
  { to: "/jobs", label: "Open Roles" },
  { to: "/job-seekers", label: "Submit CV" },
  { to: "/contact", label: "Contact" },
];

const SERVICES = [
  "Manpower Recruitment",
  "Project Staffing",
  "Role-Based Hiring",
  "Documentation & Visa",
  "Post-Deployment Support",
];

const VIEW_PARK_TOWERS_MAPS_URL =
  "https://www.google.com/maps/search/?api=1&query=View+Park+Towers,+Utaali+Lane,+Nairobi";

export default function SiteFooter() {
  return (
    <footer className="site-footer">
      {/* Wordmark band — big confident brand statement */}
      <div className="site-footer__wordmark-band">
        <div className="container">
          <h2 className="site-footer__wordmark">
            Connecting Kenya.{" "}
            <span className="site-footer__wordmark-accent">
              Supporting the Gulf.
            </span>
          </h2>
          <p className="site-footer__wordmark-sub">
            Placing 5,000+ careers. Standing alongside 50+ employers across 17
            industries — every step of recruitment.
          </p>
        </div>
      </div>

      <div className="container">
        <div className="site-footer__grid">
          {/* Column 1: Brand + contact summary */}
          <div className="site-footer__col site-footer__col--brand">
            <Link to="/" className="site-footer__logo" aria-label="Adel Group home">
              <span className="site-footer__logo-mark">A</span>
              <span className="site-footer__logo-text">
                <span>ADEL</span>
                <span className="site-footer__logo-text-accent">GROUP</span>
              </span>
            </Link>

            <p className="site-footer__brand-line">
              Kenya-based recruitment partner placing quality candidates with
              Middle East employers across construction, hospitality, logistics,
              healthcare, and beyond.
            </p>

            <ul className="site-footer__social">
              <li>
                <a
                  href="https://facebook.com/adelgroup"
                  className="site-footer__social-link"
                  aria-label="Facebook"
                  target="_blank"
                  rel="noopener noreferrer"
                  data-cursor="link"
                >
                  <Facebook size={16} strokeWidth={1.8} />
                </a>
              </li>
              <li>
                <a
                  href="https://linkedin.com/company/adelgroup"
                  className="site-footer__social-link"
                  aria-label="LinkedIn"
                  target="_blank"
                  rel="noopener noreferrer"
                  data-cursor="link"
                >
                  <Linkedin size={16} strokeWidth={1.8} />
                </a>
              </li>
              <li>
                <a
                  href="https://twitter.com/adelgroup"
                  className="site-footer__social-link"
                  aria-label="Twitter"
                  target="_blank"
                  rel="noopener noreferrer"
                  data-cursor="link"
                >
                  <Twitter size={16} strokeWidth={1.8} />
                </a>
              </li>
            </ul>
          </div>

          {/* Column 2: Quick Links */}
          <div className="site-footer__col">
            <h4 className="site-footer__col-title">Navigate</h4>
            <ul className="site-footer__links">
              {QUICK_LINKS.map((l) => (
                <li key={l.to}>
                  <Link to={l.to} className="site-footer__link" data-cursor="link">
                    <span>{l.label}</span>
                    <ArrowUpRight size={12} strokeWidth={2} />
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Column 3: Services */}
          <div className="site-footer__col">
            <h4 className="site-footer__col-title">What we do</h4>
            <ul className="site-footer__links">
              {SERVICES.map((s) => (
                <li key={s}>
                  <Link
                    to="/services"
                    className="site-footer__link"
                    data-cursor="link"
                  >
                    <span>{s}</span>
                    <ArrowUpRight size={12} strokeWidth={2} />
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Column 4: Contact */}
          <div className="site-footer__col">
            <h4 className="site-footer__col-title">Reach us</h4>
            <ul className="site-footer__contact">
              <li>
                <a
                  href={VIEW_PARK_TOWERS_MAPS_URL}
                  className="site-footer__contact-item"
                  target="_blank"
                  rel="noopener noreferrer"
                  data-cursor="link"
                >
                  <MapPin size={14} strokeWidth={1.8} />
                  <span>
                    View Park Towers, 6th Floor
                    <br />
                    Utaali Lane, Nairobi
                  </span>
                </a>
              </li>
              <li>
                <a
                  href="tel:+254117222777"
                  className="site-footer__contact-item"
                  data-cursor="link"
                >
                  <Phone size={14} strokeWidth={1.8} />
                  <span>+254 117 222 777</span>
                </a>
              </li>
              <li>
                <a
                  href="mailto:info@adelgroups.com"
                  className="site-footer__contact-item"
                  data-cursor="link"
                >
                  <Mail size={14} strokeWidth={1.8} />
                  <span>info@adelgroups.com</span>
                </a>
              </li>
              <li>
                <div className="site-footer__contact-item">
                  <Clock size={14} strokeWidth={1.8} />
                  <span>
                    Mon&ndash;Sat, 8am&ndash;5pm
                    <br />
                    East Africa Time (GMT+3)
                  </span>
                </div>
              </li>
            </ul>
          </div>
        </div>

        <div className="site-footer__bottom">
          <div className="site-footer__legal">
            <span>&copy; {new Date().getFullYear()} Adel Group. All rights reserved.</span>
            <span className="site-footer__legal-sep" aria-hidden="true">&middot;</span>
            <span>Recruitment partner · Kenya to the Gulf</span>
          </div>
          <div className="site-footer__legal-links">
            <a href="/privacy-policy" className="site-footer__legal-link" data-cursor="link">
              Privacy
            </a>
            <a href="/terms" className="site-footer__legal-link" data-cursor="link">
              Terms
            </a>
          </div>
        </div>
      </div>
    </footer>
  );
}
